import Image from 'next/image';
import Profile from '@/assets/icons/common/avatar.svg';
import { formatDate, newsBlogFormatDate } from '@/utils/formatDate';
import { ClockIcon } from 'lucide-react';

interface ProfilePicture {
  url?: string | null;
  name?: string | null;
}

interface UpdatedByUser {
  name?: string | null;
  designation?: string | null;
  profile_picture?: ProfilePicture | null;
  author_bio?: string | null;
}

interface UpdateDateArticleProps {
  dateTime: string | null;
  updatedByUser?: UpdatedByUser | null;
}

export default function UpdateDateArticle({ dateTime, updatedByUser }: Readonly<UpdateDateArticleProps>) {
  // Check if the bio is short (1-3 words) or long (4+ words)
  const bioWords = updatedByUser?.author_bio?.trim().split(/\s+/) ?? [];
  const isShortBio = bioWords.length > 0 && bioWords.length <= 10;
  return (
    <div className="my-1 flex flex-col gap-2 text-xs text-primary-text sm:my-2">
      <div className="items-center gap-2 md:flex">
        {/* User Info Section */}
        <div className="items-center text-xs text-gray-700">
          <div className="flex items-center gap-1">
            <span className="shrink-0 text-xs text-gray-500">Updated by -</span>
            {/* always show avatar, use default if no URL */}
            <Image
              src={updatedByUser?.profile_picture?.url ?? Profile}
              alt={updatedByUser?.profile_picture?.name ?? 'Author'}
              width={18}
              height={18}
              className="shrink-0 rounded-full"
            />
            <span className="font-medium">{updatedByUser?.name ?? 'Tanya Verma'}</span>

            {/* Show Designation */}
            {updatedByUser?.designation && <span className="text-gray-500"> | {updatedByUser.designation}</span>}

            {/* Show Short Bio Inline */}
            {isShortBio && updatedByUser?.author_bio && (
              <span className="text-gray-500"> | {updatedByUser.author_bio}</span>
            )}
            {dateTime && (
              <div className="ml-4 hidden items-center gap-1 text-gray-500 sm:flex">
                <ClockIcon size={16} />
                <span>Updated on - {newsBlogFormatDate(dateTime)}</span>
              </div>
            )}
          </div>

          {/* Show Long Bio Below */}
          {!isShortBio && updatedByUser?.author_bio && (
            <div className="text-justify text-gray-500 sm:ml-20 md:mt-1">{updatedByUser.author_bio}</div>
          )}
        </div>
      </div>

      {/* Updated Date Section */}
      {dateTime && (
        <div className="mt-1 block items-center gap-1 text-gray-500 sm:hidden">
          <ClockIcon className="hidden sm:block" />
          <span>Updated on - {newsBlogFormatDate(dateTime)}</span>
        </div>
      )}
    </div>
  );
}
